// frontend/src/hooks/useFeatures.ts
// Kullanıcının aktif feature'larını backend'den çeker ve cache'ler.
// Kullanım: const { hasFeature, isLoading } = useFeatures();

import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/auth.service';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

// Modül seviyesi cache — sayfa yenilenene kadar korunur
let featuresCache: Set<string> | null = null;
let cacheUserId: string | null = null;
let fetchPromise: Promise<Set<string>> | null = null;

const ALL_SYSTEM_FEATURES = [
  'course.access', 'course.premium', 'lesson.preview', 'lesson.premium',
  'ai.chat', 'ai.mentor', 'ai.code_review', 'ai.code_fix',
  'ai.project_generator', 'ai.quiz_generator', 'ai.learning_plan',
  'certificate.generate', 'hackathon.join', 'parent.report',
  'teacher.panel', 'school.dashboard', 'student.management',
  'api.access', 'adventure.mode', 'challenge.premium', 'report.pdf', 'report.excel'
];

async function fetchFeatures(userId: string): Promise<Set<string>> {
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE}/api/billing/my-features`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Feature listesi alınamadı');
      const data = await res.json();
      return new Set<string>(data.features ?? []);
    } catch {
      // Hata durumunda free plan feature'ları varsayılan olarak ver
      return new Set(['course.access', 'lesson.preview', 'ai.chat']);
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

export function useFeatures() {
  const { session, profile } = useAuthStore();
  const userId = session?.user.id;
  const isAdmin = profile?.role === 'admin';

  const [features, setFeatures] = useState<Set<string>>(() => {
    if (isAdmin) return new Set(ALL_SYSTEM_FEATURES);
    if (cacheUserId === userId && featuresCache) return featuresCache;
    return new Set(['course.access', 'lesson.preview']);
  });
  const [isLoading, setIsLoading] = useState(!featuresCache || cacheUserId !== userId);

  useEffect(() => {
    if (isAdmin) {
      const all = new Set(ALL_SYSTEM_FEATURES);
      featuresCache = all;
      cacheUserId = userId || 'admin';
      setFeatures(all);
      setIsLoading(false);
      return;
    }

    if (!userId) {
      // Giriş yapılmamış: free feature'lar
      setFeatures(new Set(['course.access', 'lesson.preview']));
      setIsLoading(false);
      return;
    }

    // Farklı kullanıcı → cache temizle
    if (cacheUserId !== userId) {
      featuresCache = null;
      cacheUserId = userId;
    }

    if (featuresCache) {
      setFeatures(featuresCache);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchFeatures(userId).then(keys => {
      featuresCache = keys;
      cacheUserId = userId;
      setFeatures(keys);
      setIsLoading(false);
    });
  }, [userId, isAdmin]);

  const hasFeature = useCallback(
    (featureKey: string): boolean => {
      if (isAdmin) return true;
      return features.has(featureKey);
    },
    [features, isAdmin]
  );

  const hasAnyFeature = useCallback(
    (...keys: string[]): boolean => {
      if (isAdmin) return true;
      return keys.some(k => features.has(k));
    },
    [features, isAdmin]
  );

  const hasAllFeatures = useCallback(
    (...keys: string[]): boolean => {
      if (isAdmin) return true;
      return keys.every(k => features.has(k));
    },
    [features, isAdmin]
  );

  /** Feature cache'ini temizler ve yeniden çeker (checkout sonrası kullan) */
  const refreshFeatures = useCallback(async () => {
    featuresCache = null;
    if (isAdmin) {
      const all = new Set(ALL_SYSTEM_FEATURES);
      featuresCache = all;
      setFeatures(all);
      setIsLoading(false);
      return;
    }
    if (!userId) return;
    setIsLoading(true);
    const keys = await fetchFeatures(userId);
    featuresCache = keys;
    setFeatures(keys);
    setIsLoading(false);
  }, [userId, isAdmin]);

  return { hasFeature, hasAnyFeature, hasAllFeatures, features, isLoading, refreshFeatures };
}

// Statik yardımcı: hook dışında kullanım için
export function clearFeaturesCache() {
  featuresCache = null;
  cacheUserId = null;
}
